import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem("fleuris_cart");
    return saved ? JSON.parse(saved) : [];
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("fleuris_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, variantIndex = null, delivery = null) => {
    const key = variantIndex !== null ? `${product.id}-v${variantIndex}` : product.id;
    setItems(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? {
          ...i,
          quantity: i.quantity + 1,
          deliveryDate: delivery?.date ?? i.deliveryDate,
          deliveryTime: delivery?.time ?? i.deliveryTime,
        } : i);
      }
      const price = variantIndex !== null && product.variants
        ? product.variants[variantIndex].price
        : product.price;
      const label = variantIndex !== null && product.variants
        ? product.variants[variantIndex].label
        : null;
      return [...prev, {
        key,
        id: product.id,
        name: product.name,
        image_url: product.image_url,
        price,
        variantLabel: label,
        variantIndex,
        quantity: 1,
        deliveryDate: delivery?.date || null,
        deliveryTime: delivery?.time || null
      }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((key) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);

  const updateQuantity = useCallback((key, qty) => {
    if (qty <= 0) { setItems(prev => prev.filter(i => i.key !== key)); return; }
    setItems(prev => prev.map(i => i.key === key ? { ...i, quantity: qty } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const count = items.reduce((sum, item) => sum + item.quantity, 0);

  const value = useMemo(
    () => ({ items, addItem, removeItem, updateQuantity, clearCart, total, count, isOpen, setIsOpen }),
    [items, addItem, removeItem, updateQuantity, clearCart, total, count, isOpen]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
